/**
 * Site groups: the network's own groupings of its sites, used to target or
 * report on several sites at once. Membership lives on the site, not here — a
 * site names the groups it belongs to, so a group is only its own details.
 */

import { apiFetch, toArray } from '@/services/core/apiFetch'
import { throwApiError } from '@/services/core/apiError'
import { getContext } from '@/services/networks/networksApi'
import { searchParams, statusParams, type ObjectStatus } from '@/services/core/listSearch'
import { fetchAllPages, sortParams, type ListPage, type ListSort } from '@/services/core/paging'
import { mapValidationWarnings, type ValidationWarning } from '@/services/core/validationWarnings'

const API_BASE = `${import.meta.env.VITE_ADNUNTIUS_API_BASE || 'https://api.dev.adnuntius.com/api'}/v1`
const JSON_HEADERS = { 'Content-Type': 'application/json' }

export interface SiteGroup {
  id: string
  name: string
  description: string
  objectState: string
  labels: string[]
  ownerTeamId: string
  ownerTeamName: string
  validationWarnings: ValidationWarning[]
  createTime: string
  updateTime: string
}

export interface SiteGroupInput {
  name: string
  description?: string
  labels?: string[]
  ownerTeamId?: string
  objectState?: string
}

function mapSiteGroup(r: Record<string, unknown>): SiteGroup {
  // A GET answers with `ownerTeam`, a POST with `team`, as the other objects do.
  const ownerTeam = (r.ownerTeam ?? r.team) as Record<string, unknown> | undefined
  return {
    id: (r.id as string) ?? '',
    name: (r.name as string) ?? '',
    description: (r.description as string) ?? '',
    objectState: (r.objectState as string) ?? '',
    labels: (r.labels as string[]) ?? [],
    ownerTeamId: (ownerTeam?.id as string) ?? '',
    ownerTeamName: (ownerTeam?.name as string) ?? '',
    validationWarnings: mapValidationWarnings(r.validationWarnings),
    createTime: (r.createTime as string) ?? '',
    updateTime: (r.updateTime as string) ?? '',
  }
}

function toApiBody(input: SiteGroupInput): Record<string, unknown> {
  const { ownerTeamId, ...rest } = input
  const body: Record<string, unknown> = { ...rest }
  if (ownerTeamId) body.ownerTeam = { id: ownerTeamId }
  return body
}

/** The options a site group list page is narrowed by on the server. */
export interface SiteGroupListOptions {
  page?: number
  pageSize?: number
  search?: string
  status?: ObjectStatus
  sort?: ListSort | null
}

/** One page of site groups, narrowed by search and state on the server. */
async function fetchSiteGroupPage(
  search: string | undefined,
  status: ObjectStatus | undefined,
  page: number,
  pageSize: number,
  sort?: ListSort | null
): Promise<ListPage<SiteGroup>> {
  const params = new URLSearchParams({
    ...searchParams('siteGroups', search),
    context: getContext(),
    includeHidden: 'false',
    ...statusParams(status),
    ...sortParams(sort, { orderBy: 'name', orderByDirection: 'ASCENDING' }),
    page: String(page),
    pageSize: String(pageSize),
  })
  const res = await apiFetch(`${API_BASE}/sitegroups?${params}`, { headers: JSON_HEADERS })
  if (!res.ok) await throwApiError(res, 'Failed to fetch site groups')
  const data = (await res.json()) as { totalCount?: number }
  const results = toArray<Record<string, unknown>>(data).map(mapSiteGroup)
  return { results, totalCount: data.totalCount ?? results.length, page, pageSize }
}

export const SiteGroupsApi = {
  /** One page of site groups, filtered by search and state on the server. */
  getPage: ({ page = 1, pageSize = 25, search, status, sort }: SiteGroupListOptions = {}) =>
    fetchSiteGroupPage(search, status, page, pageSize, sort),

  /** Every site group matching the filters, walked page by page — for downloads. */
  getAllPages: (search?: string, status?: ObjectStatus): Promise<SiteGroup[]> =>
    fetchAllPages((page, pageSize) => fetchSiteGroupPage(search, status, page, pageSize)),

  getAll: async (search?: string): Promise<SiteGroup[]> => {
    const params = new URLSearchParams({
      ...searchParams('siteGroups', search),
      context: getContext(),
      includeActive: 'true',
      includeHidden: 'false',
      includeInactive: 'true',
      orderBy: 'name',
      orderByDirection: 'ASCENDING',
      page: '1',
      pageSize: '100',
    })
    const res = await apiFetch(`${API_BASE}/sitegroups?${params}`, { headers: JSON_HEADERS })
    if (!res.ok) await throwApiError(res, 'Failed to fetch site groups')
    return toArray<Record<string, unknown>>(await res.json()).map(mapSiteGroup)
  },

  getById: async (id: string): Promise<SiteGroup> => {
    const res = await apiFetch(`${API_BASE}/sitegroups/${id}?context=${getContext()}`, {
      headers: JSON_HEADERS,
    })
    if (!res.ok) await throwApiError(res, 'Failed to fetch site group')
    return mapSiteGroup(await res.json())
  },

  create: async (id: string, input: SiteGroupInput): Promise<SiteGroup> => {
    const res = await apiFetch(`${API_BASE}/sitegroups/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ id, ...toApiBody(input) }),
    })
    if (!res.ok) await throwApiError(res, 'Failed to create site group')
    return mapSiteGroup(await res.json())
  },

  update: async (id: string, input: SiteGroupInput): Promise<SiteGroup> => {
    const res = await apiFetch(`${API_BASE}/sitegroups/${id}?context=${getContext()}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(toApiBody(input)),
    })
    if (!res.ok) await throwApiError(res, 'Failed to save site group')
    return mapSiteGroup(await res.json())
  },
}
